import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getMondayOfWeek, getAllWeeksFromEarliestDate, formatWeekRange } from '../../utils/timeUtils';

const WeekSelector = ({ selectedWeekMonday, onWeekChange }) => {
  const { classes } = useSelector((state) => state.classes);

  // Find the earliest date from all classes (using class createdAt or student addedAt)
  const earliestDate = useMemo(() => {
    // Start with current date, then find earliest if classes exist
    let earliest = new Date();
    let foundAnyDate = false;
    
    classes.forEach((classItem) => {
      // Check class createdAt
      if (classItem.createdAt) {
        const classDate = new Date(classItem.createdAt);
        if (!foundAnyDate || classDate < earliest) {
          earliest = classDate;
          foundAnyDate = true;
        }
      }
      
      // Check student addedAt dates
      if (classItem.students && classItem.students.length > 0) {
        classItem.students.forEach((student) => {
          if (student.addedAt) {
            const studentDate = new Date(student.addedAt);
            if (!foundAnyDate || studentDate < earliest) {
              earliest = studentDate;
              foundAnyDate = true;
            }
          } else if (classItem.createdAt) {
            // Fallback to class createdAt if student doesn't have addedAt
            const classDate = new Date(classItem.createdAt);
            if (!foundAnyDate || classDate < earliest) {
              earliest = classDate;
              foundAnyDate = true;
            }
          }
        });
      }
    });
    
    // If no dates found, use 3 months ago as default to show some history
    if (!foundAnyDate) {
      earliest = new Date();
      earliest.setMonth(earliest.getMonth() - 3);
    }
    
    return earliest;
  }, [classes]);

  // Generate all available weeks
  const availableWeeks = useMemo(() => {
    const weeks = getAllWeeksFromEarliestDate(earliestDate);
    
    // Add current week if not already included
    const currentWeekMonday = getMondayOfWeek();
    const currentWeekExists = weeks.some(
      (w) => w.monday.getTime() === currentWeekMonday.getTime()
    );
    
    if (!currentWeekExists) {
      weeks.unshift({
        monday: currentWeekMonday,
        sunday: new Date(currentWeekMonday.getTime() + 6 * 24 * 60 * 60 * 1000),
        label: formatWeekRange(currentWeekMonday, new Date(currentWeekMonday.getTime() + 6 * 24 * 60 * 60 * 1000)),
      });
    }
    
    return weeks;
  }, [earliestDate]);


  return (
    <div className="flex items-center gap-2 mb-3 sm:mb-4">
      <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
        Week:
      </label>
      <select
        value={selectedWeekMonday ? selectedWeekMonday.toISOString() : ''}
        onChange={(e) => {
          const selectedDate = e.target.value ? new Date(e.target.value) : getMondayOfWeek();
          onWeekChange(selectedDate);
        }}
        className="px-3 py-1.5 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
      >
        {availableWeeks.map((week, index) => {
          // Week 1 is the most recent week (index 0), Week 2 is second most recent, etc.
          const weekNumber = index + 1;
          return (
            <option key={week.monday.toISOString()} value={week.monday.toISOString()}>
              Week {weekNumber} ({week.label})
            </option>
          );
        })}
      </select>
    </div>
  );
};

export default WeekSelector;
